import { supabase } from "@/integrations/supabase/client";
import { BACKUP_FORMAT, BACKUP_FORMAT_VERSION, BACKUP_TABLE_SPECS, type TableSpec } from "./schema";
import { sanitizeRows } from "./sanitize";
import { payloadHash } from "./canonical";
import type { BackupData, BackupScope, ClinicalOSBackupFile, FormulationRef, SubstanceRef } from "./types";

const PAGE = 1000;

export type ProgressFn = (p: { phase: string; current?: number; total?: number }) => void;

async function fetchAllPaged(
  table: string,
  userId: string,
  filter?: (q: any) => any,
  onProgress?: ProgressFn,
  label?: string,
): Promise<Record<string, any>[]> {
  const rows: Record<string, any>[] = [];
  for (let from = 0; ; from += PAGE) {
    let query = (supabase as any).from(table).select("*").eq("owner_id", userId).range(from, from + PAGE - 1);
    if (filter) query = filter(query);
    const { data, error } = await query;
    if (error) throw new Error(`${table}: ${error.message}`);
    const batch = (data ?? []) as Record<string, any>[];
    rows.push(...batch);
    onProgress?.({ phase: `Lendo ${label ?? table}`, current: rows.length });
    if (batch.length < PAGE) break;
  }
  return rows;
}

function includeSpec(spec: TableSpec, scope: BackupScope): boolean {
  if (spec.group === "pharm_private" && !scope.includePharmacology) return false;
  if (spec.group === "derived" && !scope.includeDerived) return false;
  if (spec.group === "audit" && !scope.includeAuditHistory) return false;
  return true;
}

function chunk<T>(items: T[], size: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < items.length; i += size) out.push(items.slice(i, i + size));
  return out;
}

/** Exporta a conta (ou um paciente) para o formato ClinicalOS Backup v1. */
export async function exportBackup(
  userId: string,
  scope: BackupScope,
  onProgress?: ProgressFn,
): Promise<ClinicalOSBackupFile> {
  const tables: Record<string, Record<string, any>[]> = {};
  const groups: Record<string, any> = {};
  const collectedIds = new Map<string, Set<string>>();

  // pacientes do escopo
  onProgress?.({ phase: "Resolvendo escopo" });
  const patientRows = await fetchAllPaged("patients", userId, (q) =>
    scope.kind === "patient" && scope.patientId ? q.eq("id", scope.patientId) : q, onProgress, "pacientes");
  const patientIds = patientRows.map((p) => p.id as string);

  for (const spec of BACKUP_TABLE_SPECS) {
    if (!includeSpec(spec, scope)) continue;
    groups[spec.table] = spec.group;

    let rows: Record<string, any>[];
    if (spec.table === "patients") {
      rows = patientRows;
    } else if (scope.kind === "patient" && spec.patientScoped) {
      rows = patientIds.length
        ? await fetchAllPaged(spec.table, userId, (q) => q.in("patient_id", patientIds), onProgress, spec.label)
        : [];
    } else if (scope.kind === "patient" && spec.refs) {
      // filhas sem patient_id: filtrar pelos pais já coletados
      const [refCol, refTable] = Object.entries(spec.refs)[0] ?? [];
      const parentIds = refCol ? Array.from(collectedIds.get(refTable ?? "") ?? []) : [];
      rows = parentIds.length
        ? (await Promise.all(
            chunk(parentIds, 200).map((ids) =>
              fetchAllPaged(spec.table, userId, (q) => q.in(refCol!, ids), onProgress, spec.label),
            ),
          )).flat()
        : [];
    } else if (scope.kind === "patient" && spec.group === "audit") {
      rows = [];
    } else {
      rows = await fetchAllPaged(spec.table, userId, undefined, onProgress, spec.label);
    }

    tables[spec.table] = sanitizeRows(spec.table, rows);
    collectedIds.set(spec.table, new Set(rows.map((r) => r.id).filter(Boolean)));
  }

  // referências mínimas para re-resolver substâncias/formulações globais
  onProgress?.({ phase: "Coletando referências farmacológicas" });
  const substanceIds = new Set<string>();
  const formulationIds = new Set<string>();
  const substanceRefs: SubstanceRef[] = [];
  const formulationRefs: FormulationRef[] = [];
  for (const rows of Object.values(tables)) {
    for (const row of rows) {
      if (row.substance_id) substanceIds.add(row.substance_id);
      if (row.formulation_id) formulationIds.add(row.formulation_id);
    }
  }
  for (const ids of chunk(Array.from(substanceIds), 200)) {
    const { data } = await (supabase as any)
      .from("substances")
      .select("id,name,canonical_substance_id,is_global,synonyms")
      .in("id", ids);
    substanceRefs.push(...((data ?? []) as SubstanceRef[]));
  }
  for (const ids of chunk(Array.from(formulationIds), 200)) {
    const { data } = await (supabase as any)
      .from("substance_formulations")
      .select("id,substance_id,formulation_name,route,is_global")
      .in("id", ids);
    formulationRefs.push(...((data ?? []) as FormulationRef[]));
  }

  const data: BackupData = { tables, substanceRefs, formulationRefs, groups };
  const counts: Record<string, number> = {};
  for (const [table, rows] of Object.entries(tables)) counts[table] = rows.length;

  onProgress?.({ phase: "Gerando checksum" });
  const hash = await payloadHash(data);

  return {
    format: BACKUP_FORMAT,
    formatVersion: BACKUP_FORMAT_VERSION,
    app: "ClinicalOS",
    exportedAt: new Date().toISOString(),
    exportedBy: userId,
    scope,
    counts,
    integrity: { algorithm: "SHA-256", payloadHash: hash },
    excluded: [
      "segredos de integração (secret_config_reference, tokens, chaves)",
      "credenciais e sessões de autenticação",
      "dados de outros usuários",
      "base farmacológica global (exportada apenas como referência id + nome)",
    ],
    data,
  };
}

export function backupFileName(date = new Date()): string {
  const iso = date.toISOString().replace(/\.\d{3}Z$/, "").replace(/:/g, "-");
  return `clinicalos-backup-${iso}.json`;
}

export function downloadBackupFile(file: ClinicalOSBackupFile): { size: number; name: string } {
  const text = JSON.stringify(file);
  const blob = new Blob([text], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const name = backupFileName(new Date(file.exportedAt));
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
  return { size: blob.size, name };
}
